// ─────────────────────────────────────────────────────────────────────────
// RoleFormPermissionsEditor — Role Configure → Form tab.
//
// Unlike ModuleFieldConfigEditor (List View / Filter tabs, an independently
// seeded flat field list), this tab's structure comes straight from Form
// Groups: GET /admin/role-form-permissions derives the group/field tree from
// the module's current Form Groups config and overlays this role's saved
// grants. Form Groups stays the single source of truth — new/renamed/removed
// groups and fields sync here automatically, and a role can only ever narrow
// what's globally enabled, never widen it (enforced again server-side on save
// via ClampFormPermissionFieldsToGlobal).
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Select, Space, Spin, Switch, Tabs, Tooltip, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { getAdminClients, getAvailableModules, getRoleFormPermissions, saveRoleFormPermissions } from '../../../../services/adminApi';
import { normalizeModuleOptions } from '../../../../utils/moduleOptions';
import '../../../../styles/UserSettingsSection.css';
import '../../../../styles/FormGroupsSection.css';

const { Text } = Typography;

function withGroupEnabled(group, enabled) {
  return {
    ...group,
    enabled,
    fields: group.fields.map((f) => ({ ...f })),
  };
}

export default function RoleFormPermissionsEditor({ role }) {
  const [moduleTabs, setModuleTabs]     = useState([]);
  const [activeModule, setActiveModule] = useState('');
  const [groups, setGroups]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [clients, setClients]           = useState([]);
  // Same client/region scope Form Groups itself supports (GetFormGroupConfigScoped
  // is generic — not restricted to a hardcoded module), so a client-scoped Form
  // Groups override is reflected in this role's permissions too, whichever
  // module it was configured for.
  const [scope, setScope]               = useState({ clientId: '', region: '' });

  const loadModules = useCallback(async () => {
    const raw = await getAvailableModules().catch(() => []);
    return normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      loadModules().then((tabs) => {
        if (cancelled) return;
        setModuleTabs(tabs);
        setActiveModule((current) => (tabs.some((t) => t.key === current) ? current : tabs[0]?.key ?? ''));
      });
      getAdminClients({ limit: 200 }).then((data) => { if (!cancelled) setClients(data); }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, [loadModules]);

  const loadGroups = useCallback(async (module, currentScope) => {
    if (!role || !module) { setGroups([]); return; }
    setLoading(true);
    try {
      const data = await getRoleFormPermissions(role, module, currentScope);
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load form permissions');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    queueMicrotask(() => { loadGroups(activeModule, scope); });
  }, [activeModule, scope, loadGroups]);

  function toggleGroup(groupName, enabled) {
    setGroups((prev) => prev.map((g) => (g.name === groupName ? withGroupEnabled(g, enabled) : g)));
  }

  function toggleField(groupName, fieldKey, enabled) {
    setGroups((prev) => prev.map((g) => (
      g.name !== groupName ? g : {
        ...g,
        fields: g.fields.map((f) => (f.field === fieldKey ? { ...f, enabled } : f)),
      }
    )));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveRoleFormPermissions(role, activeModule, groups);
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Spin spinning={loading}>
      <Space wrap style={{ marginBottom: 8 }}>
        <Select
          size="small"
          allowClear
          showSearch
          placeholder="Client (optional — matches a client-scoped Form Groups override)"
          style={{ width: 320 }}
          value={scope.clientId || undefined}
          optionFilterProp="label"
          options={clients.map((c) => ({
            value: c._id ?? c.id,
            label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
          })).filter((c) => c.value)}
          onChange={(clientId) => setScope((prev) => ({ ...prev, clientId: clientId ?? '' }))}
        />
      </Space>
      <Tabs
        size="small"
        activeKey={activeModule}
        onChange={setActiveModule}
        items={moduleTabs.map((t) => ({ key: t.key, label: t.label }))}
      />
      <div className="fg-expanded-fields" style={{ marginTop: 8 }}>
        {groups.length === 0 && !loading && <Empty description="No form groups configured for this module." />}
        {groups.map((group) => (
          <div key={group.name} className="fg-field-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div className="fg-field-card-inner" style={{ padding: '8px 12px' }}>
              <span className="uss-field-name" style={{ flex: 1 }}>
                <strong>{group.label}</strong>
                {group.locked && (
                  <Tooltip title="Every field in this group is disabled globally in Form Groups">
                    <LockOutlined style={{ marginLeft: 6, color: 'var(--color-text-muted)' }} />
                  </Tooltip>
                )}
              </span>
              <Switch
                size="small"
                checked={group.enabled}
                disabled={group.locked}
                checkedChildren="On"
                unCheckedChildren="Off"
                onChange={(v) => toggleGroup(group.name, v)}
              />
            </div>
            <div className="fg-expanded-fields" style={{ padding: '4px 12px 10px 28px', opacity: group.enabled ? 1 : 0.5 }}>
              {group.fields.map((field) => (
                <div key={field.field} className="fg-field-row" style={{ justifyContent: 'space-between' }}>
                  <span className="fg-field-row-info">
                    <span className="fg-field-row-label">{field.label}</span>
                    <span className="fg-field-row-key">{field.field}</span>
                    {field.locked && (
                      <Tooltip title="Disabled globally in Form Groups — a role can never re-enable it">
                        <LockOutlined style={{ color: 'var(--color-text-muted)' }} />
                      </Tooltip>
                    )}
                  </span>
                  <Switch
                    size="small"
                    checked={field.enabled}
                    disabled={field.locked || !group.enabled}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={(v) => toggleField(group.name, field.field, v)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {groups.length > 0 && (
        <div className="uss-modal-footer">
          <Button type="primary" size="small" loading={saving} onClick={handleSave}>
            Save {moduleTabs.find((t) => t.key === activeModule)?.label}
          </Button>
        </div>
      )}
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        Groups and fields are read from Form Groups. A role can only turn OFF what's already
        enabled there — never turn ON a field or group disabled globally.
      </Text>
    </Spin>
  );
}
